import { ref, computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { sampleRoadmaps } from '@/lib/sample';
import {
  normaliseRoadmap,
  sanitiseRoadmap,
  makeProject,
  fitBlocksToSettings,
  freeRange,
  totalSlots,
  planOf,
  uid
} from '@/lib/schema';

// In-memory version of the app store (src/stores/roadmaps.js in the app). Same mutations and the
// same undo model, but nothing is persisted: the demo resets on reload.
const HISTORY_LIMIT = 100;
const clone = (value) => JSON.parse(JSON.stringify(value));
const snapshotOf = (rm) => ({ name: rm.name, ...planOf(rm) });

export const useRoadmapsStore = defineStore('roadmaps', () => {
  const roadmaps = ref([]);
  const ready = ref(false);
  const lastPhase = ref({});
  const history = reactive({});

  const sorted = computed(() => [...roadmaps.value].sort((a, b) => b.createdAt.localeCompare(a.createdAt)));

  const byId = (id) => roadmaps.value.find((r) => r.id === id) || null;

  const revision = ref(0);

  function persist(rm, { touch = true } = {}) {
    if (touch) rm.updatedAt = new Date().toISOString();
    revision.value++;
  }

  function stackFor(id) {
    if (!history[id]) history[id] = { past: [], future: [] };
    return history[id];
  }

  function mutate(id, fn, { track = true } = {}) {
    const rm = byId(id);
    if (!rm) return undefined;
    const before = track ? snapshotOf(rm) : null;
    const result = fn(rm);
    if (track && JSON.stringify(before) !== JSON.stringify(snapshotOf(rm))) {
      const stack = stackFor(id);
      stack.past.push(before);
      if (stack.past.length > HISTORY_LIMIT) stack.past.shift();
      stack.future = [];
    }
    persist(rm);
    return result;
  }

  function restore(rm, snap) {
    Object.assign(rm, clone(snap));
    Object.assign(rm, sanitiseRoadmap(rm));
    persist(rm);
  }

  function undo(id) {
    const rm = byId(id);
    const stack = history[id];
    if (!rm || !stack?.past.length) return false;
    stack.future.push(snapshotOf(rm));
    restore(rm, stack.past.pop());
    return true;
  }

  function redo(id) {
    const rm = byId(id);
    const stack = history[id];
    if (!rm || !stack?.future.length) return false;
    stack.past.push(snapshotOf(rm));
    restore(rm, stack.future.pop());
    return true;
  }

  const canUndo = (id) => Boolean(history[id]?.past.length);
  const canRedo = (id) => Boolean(history[id]?.future.length);

  function load() {
    roadmaps.value = sampleRoadmaps().map((r) => normaliseRoadmap(r));
    lastPhase.value = {};
    for (const key of Object.keys(history)) delete history[key];
    ready.value = true;
  }

  function updateDetails(id, { name, settings, members, phases, milestones }) {
    return mutate(id, (rm) => {
      if (name !== undefined) rm.name = String(name).trim() || rm.name;
      if (members) {
        rm.members = members
          .map((m) => ({ id: m.id, name: m.name.trim(), capacity: m.capacity, away: [...(m.away || [])] }))
          .filter((m) => m.name);
        const valid = new Set(rm.members.map((m) => m.id));
        for (const p of rm.projects) p.memberIds = p.memberIds.filter((mid) => valid.has(mid));
      }
      if (phases) {
        rm.phases = phases.map((p) => ({ id: p.id, name: p.name.trim(), color: p.color })).filter((p) => p.name);
      }
      if (milestones) rm.milestones = milestones.map((m) => ({ ...m }));
      if (settings) {
        rm.settings = { ...rm.settings, ...settings };
        rm.projects = fitBlocksToSettings(rm.projects, rm.settings).projects;
      }
      Object.assign(rm, sanitiseRoadmap(rm));
    });
  }

  function addProject(id, data, index = null) {
    return mutate(id, (rm) => {
      const project = makeProject(data);
      if (index === null || index >= rm.projects.length) rm.projects.push(project);
      else rm.projects.splice(index, 0, project);
      Object.assign(rm, sanitiseRoadmap(rm));
      return rm.projects.find((p) => p.id === project.id);
    });
  }

  function updateProject(id, projectId, patch) {
    return mutate(id, (rm) => {
      const p = rm.projects.find((x) => x.id === projectId);
      if (!p) return;
      if (patch.name !== undefined) p.name = String(patch.name).trim() || 'Untitled project';
      if (patch.description !== undefined) p.description = String(patch.description);
      if (patch.memberIds) p.memberIds = [...patch.memberIds];
      if (patch.blocks) p.blocks = patch.blocks.map((b) => ({ ...b, id: b.id || uid() })).sort((a, b) => a.start - b.start);
      if (patch.group !== undefined) p.group = String(patch.group || '').trim();
      if (patch.status !== undefined) p.status = patch.status || '';
      if (patch.links) p.links = patch.links.map((l) => ({ ...l }));
      if (patch.dependsOn) p.dependsOn = [...patch.dependsOn];
      if (patch.allocation) p.allocation = { ...patch.allocation };
      Object.assign(rm, sanitiseRoadmap(rm));
    });
  }

  function removeProject(id, projectId) {
    return mutate(id, (rm) => {
      rm.projects = rm.projects.filter((p) => p.id !== projectId);
      for (const p of rm.projects) p.dependsOn = (p.dependsOn || []).filter((d) => d !== projectId);
    });
  }

  function duplicateProject(id, projectId) {
    return mutate(id, (rm) => {
      const idx = rm.projects.findIndex((p) => p.id === projectId);
      if (idx === -1) return null;
      const src = rm.projects[idx];
      const copy = makeProject({ ...clone(src), name: `${src.name} (copy)`, blocks: src.blocks.map((b) => ({ ...b, id: null })) });
      rm.projects.splice(idx + 1, 0, copy);
      return copy;
    });
  }

  function moveProject(id, from, to) {
    return mutate(id, (rm) => {
      if (from === to || from < 0 || from >= rm.projects.length) return;
      const [p] = rm.projects.splice(from, 1);
      rm.projects.splice(Math.max(0, Math.min(to, rm.projects.length)), 0, p);
    });
  }

  function placeProject(id, projectId, { beforeId = null, afterId = null, group } = {}) {
    return mutate(id, (rm) => {
      const from = rm.projects.findIndex((p) => p.id === projectId);
      if (from === -1) return;
      const [p] = rm.projects.splice(from, 1);
      if (group !== undefined) p.group = group;
      let to = rm.projects.length;
      if (beforeId && beforeId !== projectId) {
        const i = rm.projects.findIndex((x) => x.id === beforeId);
        if (i !== -1) to = i;
      } else if (afterId && afterId !== projectId) {
        const i = rm.projects.findIndex((x) => x.id === afterId);
        if (i !== -1) to = i + 1;
      } else if (afterId === projectId || beforeId === projectId) {
        to = from;
      }
      rm.projects.splice(to, 0, p);
    });
  }

  function renameGroup(id, from, to) {
    return mutate(id, (rm) => {
      for (const p of rm.projects) if ((p.group || '') === from) p.group = String(to || '').trim();
    });
  }

  function addBlock(id, projectId, { start, span, phaseId = null }) {
    return mutate(id, (rm) => {
      const p = rm.projects.find((x) => x.id === projectId);
      if (!p) return null;
      const range = freeRange(p, start, null, totalSlots(rm));
      if (!range) return null;
      const s = Math.max(start, range.min);
      const e = Math.min(start + span, range.max);
      if (e <= s) return null;
      const block = { id: uid(), phaseId, start: s, span: e - s };
      p.blocks.push(block);
      p.blocks.sort((a, b) => a.start - b.start);
      if (phaseId) lastPhase.value[id] = phaseId;
      return block;
    });
  }

  function updateBlock(id, projectId, blockId, patch) {
    return mutate(id, (rm) => {
      const p = rm.projects.find((x) => x.id === projectId);
      const b = p?.blocks.find((x) => x.id === blockId);
      if (!b) return;
      Object.assign(b, patch);
      if (patch.phaseId) lastPhase.value[id] = patch.phaseId;
      p.blocks.sort((a, c) => a.start - c.start);
    });
  }

  function removeBlock(id, projectId, blockId) {
    return mutate(id, (rm) => {
      const p = rm.projects.find((x) => x.id === projectId);
      if (p) p.blocks = p.blocks.filter((b) => b.id !== blockId);
    });
  }

  function defaultPhaseId(id) {
    const rm = byId(id);
    if (!rm) return null;
    const last = lastPhase.value[id];
    if (last && rm.phases.some((p) => p.id === last)) return last;
    return rm.phases[0]?.id ?? null;
  }

  return {
    roadmaps,
    revision,
    history,
    sorted,
    ready,
    byId,
    load,
    undo,
    redo,
    canUndo,
    canRedo,
    updateDetails,
    addProject,
    updateProject,
    removeProject,
    duplicateProject,
    moveProject,
    placeProject,
    renameGroup,
    addBlock,
    updateBlock,
    removeBlock,
    defaultPhaseId
  };
});
